import { HttpBase } from '@library/Security';
import { useUser } from '@domain/Security/User';
import type { ICarbucksAppTileDto } from '@domain/AppConfiguration/ICarbucksAppTile';
import type { ICarbucksProfileDto } from '@domain/Carbucks';
import type { ITransactionViewModelDto } from '@app/Carbucks/Types/ITransactionViewModel';
import type { IUser } from '@domain/Security';
import type { HttpResult } from '@library/Security';
import type { ICompleteWelcomeFlowRequest } from '../ICompleteWelcomeFlowRequest';

export const useCarbucksApi = () => new class CarbucksApi extends HttpBase
{

    /**
     * Gets the carbucks profile view model DTO
     */
    async getCarbucksProfileAsync(): Promise<ICarbucksProfileDto|null>
    {
        const user = useUser();
        const request = { appConsumerId: user?.appConsumerId, vehicleId: user?.activeVehicle.vehicleId };
        const result = await this.post( `api/carbucksprofile`, request );
        if( result.success ) return result.data;
        return null;
    }

    /**
     * Gets the cashback transactions for the user, optionally filtered to category
     * @param category The name of the category or null to retrieve all transactions
     * @returns
     */
    async getCashbackTransactions( category?: string ): Promise<ITransactionViewModelDto|null>
    {
        const user = useUser();
        const request = { appConsumerId: user?.appConsumerId, appVehicleId: user?.activeVehicle.appVehicleId, category: category };
        const result = await this.post( `api/carbucksprofile/cashback-transactions`, request );
        if( result.success ) return result.data;
        return null;
    }

    /**
     * Completes the welcome flow, using the vehicle details model
     * @param model
     * @returns
     */
    async completeWelcomeFlow( model: ICompleteWelcomeFlowRequest )
    {
        const result = await this.post( 'api/carbucksprofile/complete-welcome-flow', model );
        return result;
    }

    /**
     * Gets the Carbucks App Tile for the entered originator code, or null if doesnt exist
     * @param originatorCode
     * @param vehicleId
     * @returns
     */
    async getAppByOriginatorCodeAsync( originatorCode: string ): HttpResult<ICarbucksAppTileDto>
    {
        const result = await this.get( `api/carbucksprofile/get-app-by-originator-code/${originatorCode}` );
        return result;
    }

    /**
     * Saves the user settings to the backend
     * @returns
     */
    async saveUserSettings( user: IUser )
    {
        const payload = { appMode: user.appMode };
        const result = await this.put( 'api/carbucksprofile/save-user-settings', payload );
    }

    /**
     * Saves the users bank details
     */
    async upsertBankAccount( bsb: string, accountNumber: string )
    {
        const user = useUser();
        const request = {
            appConsumerId: user?.appConsumerId,
            bankAccount: {
                bsb,
                accountNumber
            }
        };

        const result = await this.put( 'api/carbucksprofile/bank-account', request );
        return result;
    }

    /**
     * Deletes the users bank details.
     */
    async deleteBankAccount()
    {
        const result = await this.DELETE( 'api/carbucksprofile/bank-account' );
        return result;
    }

    /**
     * Removes a linked card from the users' linked cards
     */
    async removeLinkedCard( linkedCardId: string )
    {
        const result = await this.DELETE( 'api/carbucksprofile/remove-linked-card', { linkedCardId } );
        return result;
    }
    /**
     * Redeems a set cashback amount for the user.
     * @param amount The amount that the user wants to redeem as cashback.
     */
    async redeemCashback( amount: number )
    {
        const result = await this.post( 'api/carbucksprofile/redeem-cashback', { amount } );

        // The response we will return if the request succeeded.
        type successResponse = {
            success: true,
            newBalance: number,
            amountRedeemed: number
        };

        // The response we will return if the request fails in some way.
        type failedResponse = {
            success: false,
            errorMessage: string
        };

        if ( !result.success )
        {
            return {
                success: false,
                errorMessage: result.data?.errorMessage ?? 'An unknown error occurred. Please try again'
            } as failedResponse;
        }

        return result.data as successResponse;
    }

    /**
     * Attempts to change the user to the given app
     * @param vehicleId
     * @param appConfigurationId
     * @returns
     */
    async setHomeDealershipApp( vehicleId: string, appConfigurationId: string )
    {
        const result = await this.post( 'api/carbucksprofile/change-app', { vehicleId, appConfigurationId } );
        return result;
    }
}
